import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/Header';
import ChapterList from './components/ChapterList';
import ChapterViewer from './components/ChapterViewer';
import SocialLinks from './components/SocialLinks';
import PatreonLinkGenerator from './components/PatreonLinkGenerator';
import About from './components/About';
import PostList from './components/PostList';
import './styles/App.css';
import chapters from './data/chapters';
import posts from './data/posts';
import { buildChaptersWithAssets } from './utils/buildChaptersFromAssets';
import preloadUIAssets from './utils/preloadAssets';


const SupportPage = () => <div className="main-content"><h1>Support Me</h1><p>Content coming soon...</p></div>;

function App() {
  const location = useLocation();
  const isViewer = location.pathname.startsWith('/chapter/');
  const chaptersWithAssets = useMemo(() => buildChaptersWithAssets(chapters), [chapters]);
  const [sortedChapters, setSortedChapters] = useState(() => [...chaptersWithAssets]);
  const [sortDirection, setSortDirection] = useState('desc');
  const [postSortDirection, setPostSortDirection] = useState('desc');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Preload all UI assets on mount
  useEffect(() => {
    preloadUIAssets();
  }, []);

  // Handle sidebar toggle from ChapterViewer
  const handleSidebarToggle = useCallback((isOpen) => {
    setSidebarOpen(isOpen);
  }, []);

  // Add/remove viewer-mode class on body element
  useEffect(() => {
    if (isViewer) {
      document.body.classList.add('viewer-mode');
    } else {
      document.body.classList.remove('viewer-mode');
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('viewer-mode');
    };
  }, [isViewer]);

  const sortChaptersByDate = useCallback((direction) => {
    const sorted = [...chaptersWithAssets].sort((a, b) => {
      const dateA = new Date(a.publishedAt || a.date);
      const dateB = new Date(b.publishedAt || b.date);
      if (direction === 'asc') {
        return dateA - dateB;
      } else {
        return dateB - dateA;
      }
    });
    setSortedChapters(sorted);
    setSortDirection(direction);
  }, [chaptersWithAssets]);

  useEffect(() => {
    sortChaptersByDate('desc');
  }, [sortChaptersByDate]);

  const handleSortToggle = () => {
    const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    sortChaptersByDate(newDirection);
  };

  const handlePostSortToggle = () => {
    const newDirection = postSortDirection === 'asc' ? 'desc' : 'asc';
    setPostSortDirection(newDirection);
  };

  return (
    <>
      {/* Outside viewer: show page margin bars. Inside viewer: show viewer-bar on right when sidebar open */}
      {!isViewer && (
        <>
          <div className="page-margin-bar page-margin-bar-left" aria-hidden="true" />
          <div className="page-margin-bar page-margin-bar-right" aria-hidden="true" />
        </>
      )}
      {isViewer && sidebarOpen && (
        <div className="viewer-bar viewer-bar-right" aria-hidden="true" />
      )}
        <div className={`app${isViewer ? ' viewer-active' : ''}`}>
          <div className="container">
            <Header showNavigation={!isViewer} hideLogoInViewer={isViewer} />
            <div className="content">
              <Routes>
                <Route 
                  path="/" 
                  element={
                    <div className="main-content">
                      <div className="sort-bar" onClick={handlePostSortToggle} style={{ cursor: 'pointer' }}>
                        <span>Sort by Date</span>
                        <span className="sort-icon">{postSortDirection === 'asc' ? '↑' : '↓'}</span>
                      </div>
                      <PostList posts={posts} excludeIds={['about']} sortDirection={postSortDirection} />
                    </div>
                  } 
                />
                <Route
                  path="/chapters"
                  element={
                    <>
                      <div className="main-content">
                        <div className="sort-bar" onClick={handleSortToggle} style={{ cursor: 'pointer' }}>
                          <span>Sort by Date</span>
                          <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        </div>
                        <ChapterList chapters={sortedChapters} />
                      </div>
                      <SocialLinks />
                    </>
                  }
                />
                <Route
                  path="/chapter/:chapterId/page/:pageNumber"
                  element={
                    <ChapterViewer 
                      chapters={chaptersWithAssets} 
                      onSidebarToggle={handleSidebarToggle}
                    />
                  }
                />
                <Route path="/about" element={<About />} />
                <Route path="/support" element={<SupportPage />} />
                <Route path="/patreon-links" element={<PatreonLinkGenerator chapters={chaptersWithAssets} />} />
                <Route path="*" element={<div className="main-content"><h1>404 Not Found</h1></div>} />
              </Routes>
            </div>
          </div>
        </div>
    </>
  );
}

export default App; 